import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const CONSENT_KEY = 'eulua_cookie_consent';
const CONSENT_VERSION = 1;
const OPTIONAL_STORAGE_KEYS = ['eulua_recently_viewed_products'];

export const CookieConsentContext = createContext(null);

export const readCookieConsent = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    if (saved?.version !== CONSENT_VERSION || !['accepted', 'rejected'].includes(saved?.choice)) return null;
    return saved;
  } catch {
    localStorage.removeItem(CONSENT_KEY);
    return null;
  }
};

export const allowsPersonalization = () => readCookieConsent()?.choice === 'accepted';

// Google Consent Mode: analytics_storage starts denied (see public/gtag-init.js)
// and only flips to granted once the visitor
// has actually accepted optional cookies - synced here on mount and on every
// choice change so it stays correct across tabs (storage event) too.
const syncAnalyticsConsent = (choice) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: choice === 'accepted' ? 'granted' : 'denied',
  });
};

const clearOptionalStorage = () => {
  OPTIONAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new CustomEvent('recommendationSignalsUpdated', { detail: [] }));
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(readCookieConsent);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reminderDismissed, setReminderDismissed] = useState(false);

  useEffect(() => {
    syncAnalyticsConsent(consent?.choice);
  }, [consent]);

  // Keep every open tab on the same choice. localStorage fires 'storage' in the
  // *other* tabs when one of them saves, so a visitor who accepts (or withdraws)
  // in one tab does not leave a second tab running on the previous consent
  // state - which would keep sending Analytics hits GA4 discards, or the other
  // way around.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && event.key !== CONSENT_KEY) return;
      setConsent(readCookieConsent());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Stable identity on purpose: CookieConsent.js calls this from an effect keyed
  // on the route, so a function that changed on every render would re-show the
  // reminder the instant the visitor dismissed it.
  const restoreCookieReminder = useCallback(() => setReminderDismissed(false), []);

  const saveChoice = useCallback((choice) => {
    const next = { choice, version: CONSENT_VERSION, updatedAt: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    if (choice === 'rejected') clearOptionalStorage();
    setConsent(next);
    setSettingsOpen(false);
    setReminderDismissed(false);
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: next }));
  }, []);

  const value = useMemo(() => ({
    consent,
    hasDecision: Boolean(consent),
    personalizationAllowed: consent?.choice === 'accepted',
    acceptCookies: () => saveChoice('accepted'),
    rejectNonEssential: () => saveChoice('rejected'),
    openCookieSettings: () => setSettingsOpen(true),
    closeCookieSettings: () => setSettingsOpen(false),
    dismissCookieReminder: () => setReminderDismissed(true),
    restoreCookieReminder,
    reminderDismissed,
    settingsOpen,
  }), [consent, reminderDismissed, restoreCookieReminder, saveChoice, settingsOpen]);

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => useContext(CookieConsentContext);
